import { createAssociatedTokenAccountIdempotentInstruction, NATIVE_MINT } from '@solana/spl-token';
import type { TransactionInstruction } from '@solana/web3.js';
import { PublicKey } from '@solana/web3.js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '../constants/programs.js';
import { decodeSponsorshipInstruction } from './layout.js';

export interface CanonicalAtaCreate {
  readonly payer: TransactionInstruction['keys'][number];
  readonly ata: TransactionInstruction['keys'][number];
  readonly owner: TransactionInstruction['keys'][number];
  readonly mint: TransactionInstruction['keys'][number];
  readonly systemProgram: TransactionInstruction['keys'][number];
  readonly tokenProgram: TransactionInstruction['keys'][number];
}

export interface RelayAtaSponsorshipInput {
  readonly instructions: readonly TransactionInstruction[];
  readonly relayFeePayer: PublicKey;
  readonly wallet: PublicKey;
}

export interface RelayAtaSponsorshipResult {
  readonly instructions: TransactionInstruction[];
}

/** Returns the exact standard idempotent ATA create shape or null. */
export function decodeCanonicalIdempotentAtaCreate(
  instruction: TransactionInstruction,
): CanonicalAtaCreate | null {
  const create = decodeIdempotentAtaCreate(instruction);
  if (create === null) return null;
  const expected = createAtaInstruction(create, create.payer.pubkey);
  return hasExactInstructionShape(instruction, expected) ? create : null;
}

/**
 * Decodes an idempotent ATA create from a compiled message. Solana may widen
 * signer and writable privileges when the same account appears elsewhere.
 */
export function decodeIdempotentAtaCreateFromCompiledMessage(
  instruction: TransactionInstruction,
  authenticatedWallet: PublicKey,
): CanonicalAtaCreate | null {
  const create = decodeIdempotentAtaCreate(instruction);
  if (
    create === null ||
    !create.payer.isSigner ||
    !create.payer.isWritable ||
    !create.ata.isWritable ||
    !create.owner.pubkey.equals(authenticatedWallet) ||
    !create.owner.isSigner
  ) {
    return null;
  }
  return create;
}

function decodeIdempotentAtaCreate(instruction: TransactionInstruction): CanonicalAtaCreate | null {
  if (!instruction.programId.equals(ASSOCIATED_TOKEN_PROGRAM_ID) || instruction.keys.length !== 6) {
    return null;
  }
  const [payer, ata, owner, mint, systemProgram, tokenProgram] = instruction.keys;
  if (
    payer === undefined ||
    ata === undefined ||
    owner === undefined ||
    mint === undefined ||
    systemProgram === undefined ||
    tokenProgram === undefined ||
    !systemProgram.pubkey.equals(SYSTEM_PROGRAM_ID) ||
    !tokenProgram.pubkey.equals(TOKEN_PROGRAM_ID) ||
    !ata.pubkey.equals(deriveAta(owner.pubkey, tokenProgram.pubkey, mint.pubkey))
  ) {
    return null;
  }
  const create = { payer, ata, owner, mint, systemProgram, tokenProgram };
  return bytesEqual(instruction.data, createAtaInstruction(create, payer.pubkey).data)
    ? create
    : null;
}

/**
 * Replaces eligible wallet-funded idempotent ATA creates with equivalent
 * relay-funded idempotent creates. Existing ATAs remain no-ops; creating a new
 * ATA is an accepted relay cost and does not transfer close authority.
 */
export async function sponsorRelayAtaRent(
  input: RelayAtaSponsorshipInput,
): Promise<RelayAtaSponsorshipResult> {
  const candidates = await collectRelayAtaSponsorshipCandidates(input);
  if (candidates.length === 0) return { instructions: [...input.instructions] };

  const candidateAddresses = new Set(candidates.map((candidate) => candidate.toBase58()));
  return {
    instructions: input.instructions.map((instruction) => {
      const create = decodeCanonicalIdempotentAtaCreate(instruction);
      return create !== null && candidateAddresses.has(create.ata.pubkey.toBase58())
        ? createAtaInstruction(create, input.relayFeePayer)
        : instruction;
    }),
  };
}

async function collectRelayAtaSponsorshipCandidates(
  input: Pick<RelayAtaSponsorshipInput, 'instructions' | 'wallet'>,
): Promise<PublicKey[]> {
  const userFundedAtaTargets = await userFundedBuyQuoteAtaTargets(input.instructions, input.wallet);
  return collectCandidates(input.instructions, input.wallet, userFundedAtaTargets);
}

function collectCandidates(
  instructions: readonly TransactionInstruction[],
  wallet: PublicKey,
  userFundedAtaTargets: ReadonlySet<string>,
): PublicKey[] {
  const seen = new Set<string>();
  const candidates: PublicKey[] = [];
  for (const instruction of instructions) {
    const create = decodeCanonicalIdempotentAtaCreate(instruction);
    if (create === null) continue;
    const ata = create.ata.pubkey.toBase58();
    if (
      !create.payer.pubkey.equals(wallet) ||
      !create.owner.pubkey.equals(wallet) ||
      isNativeMint(create.mint.pubkey) ||
      userFundedAtaTargets.has(ata) ||
      seen.has(ata)
    ) {
      continue;
    }
    seen.add(ata);
    candidates.push(create.ata.pubkey);
  }
  return candidates;
}

async function userFundedBuyQuoteAtaTargets(
  instructions: readonly TransactionInstruction[],
  wallet: PublicKey,
): Promise<ReadonlySet<string>> {
  const targets = new Set<string>();
  const priorSellTargets = new Set<string>();
  for (const instruction of instructions) {
    const decoded = await decodeSponsorshipInstruction(instruction);
    if (decoded === null) continue;
    const trader = decoded.account('trader');
    const quoteAta = decoded.account('trader_quote_ata');
    if (!trader?.pubkey.equals(wallet) || quoteAta === null) continue;
    if (decoded.layout.name === 'sell') {
      priorSellTargets.add(quoteAta.pubkey.toBase58());
      continue;
    }
    if (decoded.layout.name === 'buy' && !priorSellTargets.has(quoteAta.pubkey.toBase58())) {
      targets.add(quoteAta.pubkey.toBase58());
    }
  }
  return targets;
}

function createAtaInstruction(
  create: CanonicalAtaCreate,
  payer: PublicKey,
): TransactionInstruction {
  return createAssociatedTokenAccountIdempotentInstruction(
    payer,
    create.ata.pubkey,
    create.owner.pubkey,
    create.mint.pubkey,
    create.tokenProgram.pubkey,
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );
}

function hasExactInstructionShape(
  instruction: TransactionInstruction,
  expected: TransactionInstruction,
): boolean {
  return (
    instruction.programId.equals(expected.programId) &&
    bytesEqual(instruction.data, expected.data) &&
    instruction.keys.length === expected.keys.length &&
    instruction.keys.every((key, index) => {
      const expectedKey = expected.keys[index];
      return (
        expectedKey !== undefined &&
        key.pubkey.equals(expectedKey.pubkey) &&
        key.isSigner === expectedKey.isSigner &&
        key.isWritable === expectedKey.isWritable
      );
    })
  );
}

function deriveAta(owner: PublicKey, tokenProgram: PublicKey, mint: PublicKey): PublicKey {
  const [ata] = PublicKey.findProgramAddressSync(
    [owner.toBuffer(), tokenProgram.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );
  return ata;
}

function isNativeMint(mint: PublicKey): boolean {
  return mint.equals(NATIVE_MINT);
}

function bytesEqual(left: Uint8Array, right: Uint8Array): boolean {
  return left.length === right.length && left.every((byte, index) => byte === right[index]);
}
